/** @jsxImportSource @emotion/react */

import { FC } from "react";
import { css } from "@emotion/core";
import { useLocation } from "react-router-dom";
import GridCell from "ui-modules/components/Layout/Grid/GridCell";
import Grid from "ui-modules/components/Layout/Grid";
import { calculateTableData, generatedInvoiceItemList } from "../../../utils/commons";
import PaymentPanel from "../../../components/PaymentPanel";
import Text from "ui-modules/components/DataDisplay/Text";
import Img from "ui-modules/components/Image/Img";
import zitelLogo from "../../../../src/assets/images/zitel-logo.svg";

type ViewInvoiceItemType = {
  id: string;
  name: string;
  amount: number;
};

type LocationState = {
  proformaData?: any;
  agreementItemId?: string;
  selectedPeriodLabel?: string;
  timeInterval?: string;
};

const RenewProforma: FC = () => {
  const location = useLocation<LocationState>();
  const proformaData = location.state?.proformaData;
  const agreementItemId = location.state?.agreementItemId ?? "0";
  const selectedPeriodLabel = location.state?.selectedPeriodLabel;
  const timeInterval = location.state?.timeInterval ?? "";

  const invoiceItemListResp: ViewInvoiceItemType[] = proformaData?.invoiceItemList || [];
  const invoiceItemListData = invoiceItemListResp.length
    ? generatedInvoiceItemList(
        invoiceItemListResp,
        0,
        proformaData?.proformaDepositItemList || [],
        proformaData?.depositAmount || 0,
        0,
        false
      )
    : null;

  const factors: ViewInvoiceItemType[] = invoiceItemListData?.invoiceItemList ?? [];
  const totalAmount: number = invoiceItemListData?.totalAmount ?? 0;
  const totalAmountFactor: number = invoiceItemListData?.totalAmountFactor ?? 0;
  const proformaId: string = proformaData?.id ?? "";
  const customerId: string = proformaData?.customer?.id ?? "";

  const renderFactors = () => {
    if (totalAmountFactor < 0) {
      return (
        <div style={{ textAlign: "center", paddingTop: 50 }}>
          <Text type={"paragraph"}>برای پرداخت پیش فاکتور، لطفا با واحد فروش تماس بگیرید.</Text>
        </div>
      );
    }
    return (
      <>
        {(proformaData?.customer?.name || selectedPeriodLabel) && (
          <div className="customer-info">
            {proformaData?.customer?.name && (
              <div className="customer-name-row">
                <Text tag="span" className="customer-name-label">
                  نام مشترک:
                </Text>
                <Text tag="span" className="customer-name-value">
                  {proformaData.customer.name}
                </Text>
              </div>
            )}
            {selectedPeriodLabel && (
              <div className="customer-name-row">
                <Text tag="span" className="customer-name-value">
                  {selectedPeriodLabel}
                </Text>
              </div>
            )}
          </div>
        )}
        <table className="table" aria-label="simple table">
          <tbody>
            {factors.map((factor) => (
              <tr key={factor.id}>
                <th align="right" scope="row">
                  <Text>{factor.name}</Text>
                </th>
                <td align="left">
                  <Text>{calculateTableData(factor, { type: "currency", pathData: "amount" })} ریال</Text>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </>
    );
  };

  const style = css`
    .inovice-wrapper {
      height: 95vh;
      text-align: center;
      display: flex;
      flex-direction: column;
      align-items: center;
    }
    .content-wrapper {
      flex: 1;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      width: 100%;
    }
    Img {
      width: 100px;
      margin-top: 20px;
    }
    .customer-info {
      width: 100%;
      padding: 12px 20px 0;
    }
    .customer-name-row {
      display: flex;
      justify-content: flex-start;
      align-items: center;
      gap: 8px;
      direction: rtl;
      padding-bottom: 12px;
      width: 100%;
    }
    .customer-name-label {
      color: #aaa;
      font-size: 14px;
    }
    .customer-name-value {
      font-size: 14px;
    }
    table {
      margin-top: 20px;
      width: 100%;
      display: table;
      border-spacing: 0;
      border-collapse: collapse;
      tbody {
        display: table-row-group;
      }
      tr {
        color: inherit;
        display: table-row;
        outline: 0;
        vertical-align: middle;
      }
      th {
        padding-right: 20px;
        text-align: right;
        flex-direction: row-reverse;
        border-bottom: 1px solid rgba(81, 81, 81, 1);
      }
      td {
        display: table-cell;
        padding: 16px;
        font-size: 0.875rem;
        text-align: left;
        font-weight: 400;
        line-height: 1.43;
        border-bottom: 1px solid rgba(81, 81, 81, 1);
        vertical-align: inherit;
      }
      tr:last-child th,
      tr:last-child td {
        border-bottom: 0;
      }
      .handle-error {
        padding-top: 50px;
        display: inline-block;
      }
    }
  `;

  return (
    <Grid css={style}>
      <GridCell phone={4} tablet={8} desktop={12}>
        <div className="inovice-wrapper">
          <Img src={zitelLogo} alt="zitel-logo" />
          <div className="content-wrapper">
            {proformaData ? (
              <>
                {renderFactors()}
                <PaymentPanel
                  amount={totalAmountFactor < 0 ? 0 : totalAmount}
                  disabled={totalAmount <= 0 || totalAmountFactor <= 0}
                  proformasId={proformaId}
                  loading={false}
                  customerId={customerId}
                  productId={agreementItemId}
                  requiredInterval
                  timeInterval={timeInterval}
                />
              </>
            ) : (
              <div className="handle-error">
                <Text>خطایی در نمایش فاکتور رخ داده است</Text>
              </div>
            )}
          </div>
        </div>
      </GridCell>
    </Grid>
  );
};

export default RenewProforma;
